import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Category, Product } from '../types';
import { fetchCategories, fetchProducts } from '../services/api';
import CategoryCard from '../components/CategoryCard';
import ProductCard from '../components/ProductCard';
import RecentlyViewed from '../components/RecentlyViewed';
import SEO from '../components/SEO';
import { WebsiteStructuredData, OrganizationStructuredData } from '../components/StructuredData';

const HomePage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [featured, setFeatured] = useState<Product[]>([]);

  useEffect(() => {
    fetchCategories().then((all) => {
      // Feature the top-level categories (Food, Cosmetics, ...) as entry points.
      const roots = all.filter((c) => (c.parent_id ?? c.parentId ?? null) == null);
      setCategories((roots.length ? roots : all).slice(0, 6));
    });
    fetchProducts({ limit: 8 }).then(setFeatured);
  }, []);

  return (
    <div>
      <SEO title="Shop the good stuff, faster"
        description="GoodCart helps you find health-conscious products with clean ingredients, organized by category and vetted against publicly available health ratings."
        image="/og-image.png" url="https://goodcart.org/" type="website" locale="en_US" siteName="GoodCart" />
      <WebsiteStructuredData />
      <OrganizationStructuredData />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg,#f5f1e8 0%,#e5ebe3 60%,#f4f6f3 100%)' }}>
        <div className="absolute top-0 right-0 w-[28rem] h-[28rem] rounded-full opacity-25 breathe" style={{ background: 'radial-gradient(circle,#5a7d52 0%,transparent 70%)', transform: 'translate(28%,-32%)' }}></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full opacity-20 breathe" style={{ background: 'radial-gradient(circle,#a86e54 0%,transparent 70%)', transform: 'translate(-38%,42%)', animationDelay: '2s' }}></div>
        <div className="container-custom py-16 md:py-24 relative z-10">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="text-center md:text-left">
              <motion.p className="eyebrow justify-center md:justify-start mb-6" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
                Clean picks, vetted for you
              </motion.p>
              <motion.h1 className="max-w-xl mx-auto md:mx-0 text-balance" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}>
                Shop the good stuff,<br /><span className="italic text-sage-700">naturally.</span>
              </motion.h1>
              <motion.p className="mt-7 max-w-xl mx-auto md:mx-0 text-earth-700" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}>
                Hundreds of health-conscious products, sorted by clean ingredients and health ratings, so you can skip the label-reading and just add to cart.
              </motion.p>
              <motion.div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center md:justify-start" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}>
                <Link to="/categories" className="btn btn-primary">Explore the collection</Link>
                <Link to="/search" className="btn btn-outline">Browse all products</Link>
              </motion.div>
            </div>
            <motion.div
              className="hidden md:block"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              <img
                src="/images/hero-lifestyle.jpg"
                alt="A bright kitchen counter with fresh, wholesome groceries and clean everyday products"
                className="w-full rounded-3xl shadow-lift object-cover aspect-[4/3]"
                fetchPriority="high"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Philosophy strip */}
      <section className="py-6 bg-sage-700 text-cream-50">
        <div className="container-custom text-center">
          <p className="text-lg md:text-xl font-display italic" style={{ fontVariationSettings: '"opsz" 90' }}>
            "Real food and clean products, chosen with care, not marketing."
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="py-20 md:py-24">
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="eyebrow justify-center mb-3">Browse</p>
            <h2 className="text-earth-900 mb-4">Explore by category</h2>
            <div className="organic-divider max-w-xs mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((c, i) => (<CategoryCard key={c.id} category={c} index={i} size="large" />))}
          </div>
          <div className="text-center mt-12">
            <Link to="/categories" className="btn btn-outline">View all categories</Link>
          </div>
        </div>
      </section>

      {/* Featured */}
      <section className="py-20 md:py-24" style={{ background: 'linear-gradient(180deg,#fdfcfa 0%,#f5f1e8 100%)' }}>
        <div className="container-custom">
          <div className="text-center mb-12">
            <p className="eyebrow justify-center mb-3">Fresh finds</p>
            <h2 className="text-earth-900 mb-4">Trusted favorites</h2>
            <div className="organic-divider max-w-xs mx-auto"></div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {featured.map((p, i) => (<ProductCard key={p.id} product={p} index={i} />))}
          </div>
          <div className="text-center mt-12">
            <Link to="/search" className="btn btn-outline">Discover more</Link>
          </div>
        </div>
      </section>

      <RecentlyViewed />

      {/* Values */}
      <section className="py-20 md:py-24">
        <div className="container-custom">
          <div className="text-center mb-14">
            <p className="eyebrow justify-center mb-3">Why GoodCart</p>
            <h2 className="text-earth-900 mb-4">The GoodCart difference</h2>
            <div className="organic-divider max-w-xs mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              { c: 'text-sage-600', t: 'Carefully curated', d: 'Every product is evaluated against publicly available health ratings and clean-ingredient criteria.', p: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
              { c: 'text-clay-500', t: 'Time honored', d: "Skip the endless research \u2014 we've done the digging so you get only what genuinely serves you.", p: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' },
              { c: 'text-sage-600', t: 'Earth conscious', d: 'We favor sustainable practices and ethical production that respect both body and planet.', p: 'M21 12a9 9 0 11-18 0 9 9 0 0118 0z M3.6 9h16.8 M3.6 15h16.8 M12 3a15 15 0 010 18 M12 3a15 15 0 000 18' },
            ].map((v, i) => (
              <motion.div key={i} className="organic-card p-8 text-center" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }}>
                <div className={`${v.c} mx-auto mb-5`}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-14 w-14 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d={v.p} /></svg>
                </div>
                <h3 className="text-earth-900 mb-3">{v.t}</h3>
                <p className="text-earth-600 leading-relaxed">{v.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
